import { z } from "zod";
import { zfd } from "zod-form-data";
import { extendZodWithOpenApi } from "zod-openapi";
import {
    customErrorResponse,
    errorResponse,
    formBody,
    formBoolean,
    jsonBody,
    privateApi,
    successResponse,
    tagPaths,
    unref,
} from "../util.ts";
import { bucketNameMax, bucketNameMin, bucketNameRegex } from "./bucket_name.ts";
import {
    billing_account_id,
    created_at_tz,
    deleted_at,
    int,
    storage_account_uuid,
    updated_at_tz,
    user_id,
} from "./common.ts";

extendZodWithOpenApi(z);

export const bucket_name = z
    .string()
    .min(bucketNameMin)
    .max(bucketNameMax)
    .regex(bucketNameRegex)
    .describe("Globally unique S3 bucket name");

export const BucketBase = z.object({
    storage_account_uuid: storage_account_uuid.describe("Storage account the bucket is assigned to"),
    name: bucket_name,
    size_bytes: int.describe("Total stored object data in bytes"),
    billing_account_id: unref(billing_account_id).describe("Billing account charged for the bucket"),
    billing_account_id_override: unref(billing_account_id)
        .describe("Billing account override attached to this bucket (legacy)")
        .optional()
        .openapi({ deprecated: true }),
    num_objects: int.describe("Number of objects currently stored in the bucket"),
    created_at: created_at_tz,
    modified_at: updated_at_tz.optional(),
});

export const Bucket = BucketBase.extend({}).openapi({ ref: "Bucket" });

/** Legacy model for Storage [user] key. See `StorageAccountKey` */
export const StorageKey = z
    .object({
        userId: z.string(),
        accessKey: z.string(),
        secretKey: z.string(),
    })
    .openapi({ ref: "StorageKey" });

export const S3Url = z
    .object({
        url: z.string().url().openapi({ example: "https://s3-test.example.com" }),
    })
    .openapi({ ref: "S3Url" });

export const StorageAccount = z
    .object({
        uuid: storage_account_uuid,
        user_id: unref(user_id).describe("User that owns the storage account"),
        is_suspended: z.boolean().describe("Whether object storage access is suspended for this account"),
        billing_account_id: unref(billing_account_id).describe(
            "The billing account charged for resources on this storage account",
        ),
        title: z.string().describe("Descriptive text or name to distinguish this account"),
        is_deleted: z.boolean().optional().describe("Whether the storage account is deleted"),
        created_at: created_at_tz.optional(),
        deleted_at: deleted_at.optional().describe("Storage account deletion time"),
    })
    .openapi({ ref: "StorageAccount" });

export const StorageAccountCreateBody = z
    .object({
        title: StorageAccount.shape.title,
        billing_account_id: StorageAccount.shape.billing_account_id,
    })
    .openapi({ ref: "StorageAccountCreateBody" });

export const StorageAccountUpdateBody = z
    .object({
        title: StorageAccount.shape.title.optional(),
        billing_account_id: StorageAccount.shape.billing_account_id.optional(),
    })
    .openapi({ ref: "StorageAccountUpdateBody" });

export const StorageAccountKey = z
    .object({
        created_at: created_at_tz.optional(),
        access_key: z.string().describe("S3 access key"),
        secret_key: z.string().describe("S3 secret key"),
        read_only: z.boolean().describe("Whether the key only has read access"),
        description: z.string().describe("Display name for the storage account").optional(),
    })
    .openapi({ ref: "StorageAccountKey" });

export const StorageAccountKeyCreateBody = z
    .object({
        description: StorageAccountKey.shape.description,
        read_only: StorageAccountKey.shape.read_only.optional(),
    })
    .openapi({ ref: "StorageAccountKeyCreateBody" });

export const StorageAccountKeyUpdateBody = z
    .object({
        description: StorageAccountKey.shape.description,
    })
    .openapi({ ref: "StorageAccountKeyUpdateBody" });

export const StorageAccountListResponse = z.array(StorageAccount).openapi({ ref: "StorageAccountListResponse" });

export const StorageAccountKeyListResponse = z
    .array(StorageAccountKey)
    .openapi({ ref: "StorageAccountKeyListResponse" });

export const BucketListResponse = z.array(Bucket).openapi({ ref: "BucketListResponse" });

const bucketListBillingAccountId_query = unref(billing_account_id)
    .optional()
    .describe("Only return buckets billed to this account")
    .openapi({ param: { name: "billing_account_id", in: "query" } });

const accountUuid_path = storage_account_uuid.openapi({
    param: { name: "uuid", in: "path" },
});

const access_key_path = z.string().openapi({
    param: { name: "access_key", in: "path" },
});

const read_only_query = formBoolean(z.boolean().optional().default(false))
    .describe("Generate a read-only S3 key")
    .openapi({ param: { name: "read_only", in: "query" } });

/** Bucket */
export const BucketLegacyCreateBody = z
    .object({
        name: Bucket.shape.name,
        billing_account_id: zfd.numeric(billing_account_id.optional().openapi({ deprecated: true })),
    })
    .openapi({ ref: "BucketLegacyCreateBody" });

export const BucketCreateBody = z
    .object({
        name: Bucket.shape.name,
        storage_account_uuid: Bucket.shape.storage_account_uuid.optional(),
    })
    .openapi({ ref: "BucketCreateBody" });

export const BucketUpdateBody = z
    .object({
        name: Bucket.shape.name,
        billing_account_id: zfd.numeric(
            unref(billing_account_id)
                .optional()
                .describe("Legacy billing account selector; cannot be combined with storage_account_uuid")
                .openapi({ deprecated: true }),
        ),
        storage_account_uuid: Bucket.shape.storage_account_uuid.optional(),
    })
    .openapi({ ref: "BucketUpdateBody" });

export const AccessKeyLegacyDeleteBody = z
    .object({ access_key: StorageAccountKey.shape.access_key })
    .openapi({ ref: "AccessKeyLegacyDeleteBody" });

// TODO: Figure out why the error formats from the backend are not properly standardized
// TODO: Figure out if other endpoints also use this random format
const XXX_StorageErrorResponse = z
    .object({
        // Can also be completely missing (?!?) and just an empty object
        message: z.string().optional(),
    })
    .openapi({ ref: "MessageErrorResponse" });

const BucketCorsBody = z
    .object({
        name: bucket_name,
        allowed_origin: z.string().url(),
    })
    .openapi({ ref: "BucketCorsBody" });

const response_XXXError = customErrorResponse(XXX_StorageErrorResponse, { message: "IOException" });

export const storagePaths = tagPaths("object_storage")({
    "/storage/accounts": {
        get: {
            summary: "List storage accounts",
            responses: {
                ...successResponse(StorageAccountListResponse),
            },
        },
        post: {
            summary: "Create storage account",
            requestBody: jsonBody(StorageAccountCreateBody),
            responses: {
                ...successResponse(StorageAccount),
            },
        },
    },
    "/storage/accounts/{uuid}": {
        patch: {
            summary: "Update storage account",
            description: "Change a storage account title, billing account, or both.",
            parameters: [accountUuid_path],
            requestBody: jsonBody(StorageAccountUpdateBody),
            responses: {
                ...successResponse(StorageAccount),
            },
        },
        delete: {
            summary: "Delete storage account",
            description: "Also deletes associated keys. Accounts with active buckets cannot be deleted.",
            parameters: [accountUuid_path],
            responses: { ...successResponse(z.unknown()) },
        },
    },
    "/storage/accounts/{uuid}/keys": {
        get: {
            summary: "List storage account S3 access keys",
            parameters: [accountUuid_path],
            responses: {
                ...successResponse(StorageAccountKeyListResponse),
            },
        },
        post: {
            summary: "Generate storage account S3 access key",
            description: "Generate a new S3 key pair for a storage account.",
            parameters: [accountUuid_path, read_only_query],
            requestBody: jsonBody(StorageAccountKeyCreateBody),
            responses: {
                ...successResponse(StorageAccountKey),
            },
        },
    },
    "/storage/accounts/{uuid}/keys/{access_key}": {
        patch: {
            summary: "Change storage account S3 access key name",
            parameters: [accountUuid_path, access_key_path],
            requestBody: jsonBody(StorageAccountKeyUpdateBody),
            responses: {
                ...successResponse(StorageAccountKey),
            },
        },
        delete: {
            summary: "Delete storage account S3 access key",
            parameters: [accountUuid_path, access_key_path],
            responses: { ...successResponse(z.unknown()) },
        },
    },
    "/storage/bucket/list": {
        get: {
            summary: "List buckets",
            parameters: [bucketListBillingAccountId_query],
            responses: {
                ...successResponse(BucketListResponse),
                ...response_XXXError,
            },
        },
    },
    "/storage/bucket": {
        post: {
            summary: "Create bucket",
            description: `Create an S3 object storage bucket. Bucket names must be globally unique across all users. If there are multiple active storage accounts, a \`storage_account_uuid\` parameter must be provided.`,
            requestBody: jsonBody(BucketCreateBody),
            responses: {
                ...successResponse(Bucket),
                ...response_XXXError,
            },
        },
        put: {
            deprecated: true,
            summary: "Create bucket (deprecated)",
            description: `Legacy endpoint, use \`POST /storage/bucket\` instead. Bucket names must be globally unique across all users. This request cannot be used when there are multiple active storage accounts.`,
            requestBody: formBody(BucketLegacyCreateBody),
            responses: {
                ...successResponse(Bucket),
                ...response_XXXError,
            },
        },
        get: {
            summary: "Get bucket",
            requestBody: formBody(z.object({ name: bucket_name })),
            responses: {
                ...successResponse(Bucket),
                ...errorResponse("Failed to get storage bucket"),
            },
        },
        patch: {
            summary: "Update bucket",
            description: `Clear the billing account override of a bucket. Moving buckets to another storage account is not supported: \`storage_account_uuid\` must match the bucket's current storage account (otherwise \`403\` is returned). The legacy \`billing_account_id\` parameter is accepted only when there is a single storage account of the user.`,
            requestBody: formBody(BucketUpdateBody),
            responses: {
                ...successResponse(Bucket),
            },
        },
        delete: {
            summary: "Delete bucket",
            description: "Only empty buckets can be deleted via this API.",
            requestBody: formBody(z.object({ name: bucket_name })),
            responses: {
                "204": {
                    description: "Bucket deleted",
                },
            },
        },
    },
    "/storage/bucket/cors": {
        put: {
            summary: `Set CORS configuration ${privateApi}`,
            requestBody: formBody(BucketCorsBody),
            responses: {
                "200": {
                    description: "Current S3 UI CORS configuration XML",
                    content: { "application/xml": {} },
                },
            },
        },
    },
    "/storage/user/keys": {
        get: {
            deprecated: true,
            summary: "List S3 access keys. (Deprecated) ",
            description: "Deprecated; use `/storage/accounts/{uuid}/keys`.",
            responses: { ...successResponse(z.array(StorageKey)) },
        },
        post: {
            deprecated: true,
            summary: "Generate S3 access key. (Deprecated) ",
            description: "Deprecated; use `/storage/accounts/{uuid}/keys`.",
            responses: { ...successResponse(z.array(StorageKey)) },
        },
        delete: {
            deprecated: true,
            summary: "Delete S3 access key. (Deprecated)",
            description: "Deprecated; use `/storage/accounts/{uuid}/keys/{key}`.",
            requestBody: formBody(AccessKeyLegacyDeleteBody),
            responses: { ...successResponse(z.unknown()) },
        },
    },
    "/storage/api/s3": {
        get: {
            summary: "Get S3 URL",
            description: "Returns S3 API URL.",
            responses: {
                ...successResponse(S3Url),
            },
        },
    },
});
